import { estadoLegajoInfo, type EstadoLegajoInfo } from '@lib/estado-legajo';
import type { ReportePadronEstado } from '@lib/services/reportePadron';

/**
 * Opciones del filtro de estado del Reporte de padrón, en el orden del ticket
 * (VEN-512) y con sus palabras. Son las mismas que manda el server en
 * `estadoLabel`, así el filtro y la columna dicen lo mismo.
 */
export const OPCIONES_ESTADO_PADRON: Array<{ value: ReportePadronEstado; label: string }> = [
  { value: 'error', label: 'Error' },
  { value: 'pendiente_validacion_medicus', label: 'Pendiente validación' },
  { value: 'creado', label: 'Pendiente onboarding' },
  { value: 'en_validacion', label: 'Onboarding en curso' },
  { value: 'validado_por_titular', label: 'Listo para el alta' },
  { value: 'en_alta_binary', label: 'Enviado a Binary' },
  { value: 'dado_de_alta', label: 'Dado de alta' },
  { value: 'rechazado_binary', label: 'Rechazado por Binary' },
  { value: 'bloqueado', label: 'Bloqueado' },
  { value: 'espera_activacion_titular', label: 'Espera activación titular' },
  { value: 'integrantes_a_sumar', label: 'Integrantes a sumar' },
  { value: 'rechazado_medicus', label: 'Rechazado por Medicus' },
  { value: 'dado_de_baja', label: 'Dado de baja' },
];

/**
 * Los estados que NO son un estado del legajo. Los demás toman tono y tooltip
 * del catálogo de estados del legajo (`estado-legajo.ts`), que es la fuente de
 * cómo se pinta cada etapa en todo el portal.
 */
const PROPIOS: Partial<Record<ReportePadronEstado, Omit<EstadoLegajoInfo, 'label'>>> = {
  bloqueado: {
    descripcion: 'La persona está frenada individualmente: no se envía a Binary.',
    tono: 'error',
  },
  error: {
    descripcion:
      'Sus datos no pasan la validación del padrón; hay que corregirlos para que avance.',
    tono: 'error',
  },
  espera_activacion_titular: {
    descripcion:
      'Integrante a sumar a un titular ya dado de alta: espera que el titular complete la activación.',
    tono: 'warning',
  },
  // Es el legajo bis que no espera al titular: mismo tono y explicación que el
  // estado `legajo_bis` del catálogo, que también se llama "Integrantes a sumar".
  integrantes_a_sumar: estadoLegajoInfo('legajo_bis'),
};

/**
 * Cómo se pinta el estado de una persona del reporte. El `label` es el que manda
 * el server; si no vino (backend viejo), cae al de las opciones del filtro.
 */
export function estadoReportePadronInfo(
  estado: ReportePadronEstado,
  label?: string,
): EstadoLegajoInfo {
  const base = PROPIOS[estado] ?? estadoLegajoInfo(estado);
  const nombre = label || OPCIONES_ESTADO_PADRON.find((o) => o.value === estado)?.label || estado;
  return { descripcion: base.descripcion, tono: base.tono, label: nombre };
}
